import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OpticalJobDetail } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { apiErrorCode, apiErrorDetails } from '@/services/apiClient';
import { stableStringify } from '@/services/operationKey';
import { useReplaceJobPrescriptionMutation } from '../../api/opticalJobs.api';
import {
  builderReducer,
  createDraft,
  prescriptionInput,
  stepComplete,
  type BuilderDraft,
} from '../../builder/draft';
import { problemsFromRefusal, type BuilderProblem } from '../../builder/refusals';
import { PrescriptionStep } from '../../steps/PrescriptionStep';
import { trackingErrorMessage } from '../../tracking/errors';
import { externalFormOf } from '../../tracking/prescription';

interface PrescriptionCorrectionProps {
  job: OpticalJobDetail;
  customerId: string;
  /** Start on the clinical list, for a job whose clinical prescription is no longer usable. */
  clinicalFirst: boolean;
  onSaved: () => void;
  onClose: () => void;
  /** Whether anything has been changed, for the leave warning. */
  onDirtyChange: (dirty: boolean) => void;
  /** Someone else changed the job: drop this change and read the job again, never resend it. */
  onConflict: () => void;
}

/** The builder's own draft for one pair, started from the job's prescription: an external one exactly as recorded, a clinical one with nothing chosen for the user. */
function startFrom(job: OpticalJobDetail, clinicalFirst: boolean): BuilderDraft {
  const draft = createDraft();
  const pairId = draft.pairs[0]?.id ?? '';
  const rx = job.prescription;
  if (!clinicalFirst && rx?.source === 'EXTERNAL_PRESCRIPTION') {
    const external = builderReducer(draft, { type: 'chooseSource', pairId, source: 'EXTERNAL' });
    return builderReducer(external, { type: 'editExternal', pairId, patch: externalFormOf(rx) });
  }
  return builderReducer(draft, { type: 'chooseSource', pairId, source: 'CLINICAL' });
}

/**
 * Replaces the whole prescription of a job still ORDERED, with the same step the builder uses.
 *
 * A clinical prescription is chosen by its exact number from the ones usable today and sent by id alone; nothing is chosen for the user, and a replaced one is never followed. An external one starts from exactly what was recorded. The save names the version on screen, so a job someone else changed meanwhile is refused and read again rather than overwritten. Nothing typed here is kept anywhere but in memory.
 */
export function PrescriptionCorrection({
  job,
  customerId,
  clinicalFirst,
  onSaved,
  onClose,
  onDirtyChange,
  onConflict,
}: PrescriptionCorrectionProps) {
  const { t } = useTranslation();
  const [initial] = useState(() => startFrom(job, clinicalFirst));
  const [draft, dispatch] = useReducer(builderReducer, initial);
  const [start] = useState(() => {
    const first = initial.pairs[0];
    return stableStringify(first ? prescriptionInput(first.prescription) : null);
  });
  const [showErrors, setShowErrors] = useState(false);
  const [problems, setProblems] = useState<BuilderProblem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const replace = useReplaceJobPrescriptionMutation(job.id);
  // A second click lands before the button re-renders as busy: only the first may send.
  const inFlight = useRef(false);
  const pair = draft.pairs[0];

  const input = pair ? prescriptionInput(pair.prescription) : null;
  const dirty = useMemo(() => stableStringify(input) !== start, [input, start]);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  if (!pair) return null;

  const save = () => {
    if (inFlight.current) return;
    setError(null);
    setProblems([]);
    if (!stepComplete(pair, 'prescription') || !input) {
      setShowErrors(true);
      return;
    }
    inFlight.current = true;
    replace.mutate(
      { version: job.version, prescription: input },
      {
        onSettled: () => {
          inFlight.current = false;
        },
        onSuccess: () => {
          onDirtyChange(false);
          onSaved();
        },
        onError: (caught) => {
          if (apiErrorCode(caught) === 'OPTICAL_JOB_VERSION_CONFLICT') {
            onDirtyChange(false);
            onConflict();
            return;
          }
          setError(trackingErrorMessage(t, caught));
          // The server names fields from the prescription down; read them as the builder reads a pair's.
          setProblems(
            problemsFromRefusal(
              {
                code: apiErrorCode(caught),
                details: apiErrorDetails(caught).map((detail) => ({
                  ...detail,
                  field: detail.field ? `opticalJobs[0].${detail.field}` : detail.field,
                })),
              },
              [pair.id],
            ),
          );
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader
        title={t('opticalOrders.job.correctTitle')}
        subtitle={t('opticalOrders.job.correctHint', { reference: job.reference })}
      />
      <div className="space-y-4 px-5 py-4">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <PrescriptionStep
          customerId={customerId}
          pair={pair}
          copyFrom={[]}
          canAddPair={null}
          dispatch={dispatch}
          problems={problems}
          showErrors={showErrors}
        />
        <div className="border-line flex flex-wrap justify-end gap-2 border-t pt-4">
          <Button variant="secondary" onClick={onClose} disabled={replace.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={save} isLoading={replace.isPending} disabled={!dirty}>
            {t('opticalOrders.job.savePrescription')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
